import React, { useState } from "react";
import Sidebar from "../../components/Sidebar";
import "./new_invoice_company.css";
import {
  FaSave,
  FaTimes,
  FaFileInvoiceDollar,
  FaHashtag,
  FaPoundSign,
  FaSpinner,
  FaCloudUploadAlt,
  FaCheckCircle,
  FaMagic,
} from "react-icons/fa";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  createNewInvoiceApi,
  takeImageUploadSalesApi,
} from "../../services/apiCall";

export default function NewInvoiceCompanyPage() {
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [grossAmount, setGrossAmount] = useState("");
  const [netAmount, setNetAmount] = useState("");
  const [vat, setVat] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isExtracting, setIsExtracting] = useState(false);
  const [extracted, setExtracted] = useState(false);

  const userId = useSelector((state) => state?.user?.userId);
  const businessId = useSelector((state) => state?.user?.businessId);
  // Fix: get businessName from Redux (was hardcoded before)
  const businessName =
    useSelector((state) => state?.user?.businessName) || "Your Business";

  const handleAutoGenerateInvoiceNo = () => {
    const now = Date.now().toString().slice(-6);
    setInvoiceNumber(`INV-${now}`);
  };

  const handleSave = async () => {
    if (!invoiceNumber || !grossAmount || !netAmount || !vat) {
      toast.error("Please fill all required fields.");
      return;
    }

    const gross = parseFloat(grossAmount);
    if (isNaN(gross) || gross <= 0) {
      toast.error("Gross amount must be a positive number.");
      return;
    }

    const payload = {
      businessId,
      createdBy: userId,
      invoiceNumber,
      gross: parseFloat(grossAmount),
      net: parseFloat(netAmount),
      vat: parseFloat(vat),
    };
    console.log("payload::::", payload);

    try {
      setIsLoading(true);
      const response = await createNewInvoiceApi(payload);
      console.log("response:::", response);
      if (response?.data) {
        toast.success(
          `Invoice #${response.data.expense?.invoiceNumber || ""} created successfully!`,
        );
        setInvoiceNumber("");
        setGrossAmount("");
        setNetAmount("");
        setVat("");
        setImagePreview(null);
        setImage(null);
        setExtracted(false);
      } else {
        toast.error("Unexpected server response.");
      }
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "Something went wrong. Try again.";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
    setImage(file);
    setIsExtracting(true);
    setExtracted(false);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("userId", userId);
    formData.append("businessId", businessId);
    formData.append("type", "sale");

    try {
      const response = await takeImageUploadSalesApi(formData);
      const data = response?.data?.data;

      if (data) {
        if (data.gross) setGrossAmount(data.gross.toString());
        if (data.vat) setVat(data.vat.toString());
        if (data.net) setNetAmount(data.net.toString());
        if (data.invoiceNumber) setInvoiceNumber(data.invoiceNumber);
        setExtracted(true);
        toast.success("Invoice scanned — fields auto-filled!");
      }
    } catch (error) {
      console.error("Upload failed:", error.response?.data || error);
      toast.error("Failed to extract data from invoice.");
    } finally {
      setIsExtracting(false);
    }
  };

  const today = new Date().toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });

  return (
    <div className="app">
      <Sidebar />
      <main className="main">
        {/* Header */}
        <div className="dashboard-header">
          <div>
            <h2 className="dashboard-title">New Invoice</h2>
            <p className="dashboard-sub">
              Create and record a new sales invoice
            </p>
          </div>
          <div className="dashboard-info">
            <h3 className="dashboard-company">{businessName}</h3>
            <p className="dashboard-date">{today}</p>
          </div>
        </div>

        {/* Card */}
        <div className="invoice-forms">
          <div className="card-header-strip">
            <FaFileInvoiceDollar />
            <h3>Invoice Details</h3>
          </div>

          <div className="invoice-grid">
            {/* Invoice Number */}
            <div className="form-group inv-row">
              <label>
                Invoice Number <span style={{ color: "#e63946" }}>*</span>
              </label>
              <div className="inv-input-wrap">
                <div className="input-icon-wrap" style={{ flex: 1 }}>
                  <FaHashtag className="input-icon" />
                  <input
                    type="text"
                    placeholder="e.g. INV-001"
                    value={invoiceNumber}
                    onChange={(e) => setInvoiceNumber(e.target.value)}
                    style={{ paddingLeft: "2.6rem" }}
                  />
                </div>
                <button
                  type="button"
                  className="auto-gen-btn"
                  onClick={handleAutoGenerateInvoiceNo}
                  title="Auto-generate invoice number"
                >
                  <FaMagic /> Auto
                </button>
              </div>
            </div>

            {/* Empty for grid balance on mobile */}
            <div
              className="form-group"
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-end",
              }}
            >
              {/* spacer — or could put client name here in future */}
            </div>

            {/* Gross Amount */}
            <div className="form-group">
              <label>
                Gross Amount <span style={{ color: "#e63946" }}>*</span>
              </label>
              <div className="input-icon-wrap">
                <FaPoundSign className="input-icon" />
                <input
                  type="number"
                  placeholder="0.00"
                  value={grossAmount}
                  onChange={(e) => setGrossAmount(e.target.value)}
                  style={{ paddingLeft: "2.6rem" }}
                />
              </div>
            </div>

            {/* VAT */}
            <div className="form-group">
              <label>
                VAT Tax <span style={{ color: "#e63946" }}>*</span>
              </label>
              <div className="input-icon-wrap">
                <FaPoundSign className="input-icon" />
                <input
                  type="number"
                  placeholder="0.00"
                  value={vat}
                  onChange={(e) => setVat(e.target.value)}
                  style={{ paddingLeft: "2.6rem" }}
                />
              </div>
            </div>

            {/* Net Amount */}
            <div className="form-group">
              <label>
                Net Amount <span style={{ color: "#e63946" }}>*</span>
              </label>
              <div className="input-icon-wrap">
                <FaPoundSign className="input-icon" />
                <input
                  type="number"
                  placeholder="0.00"
                  value={netAmount}
                  onChange={(e) => setNetAmount(e.target.value)}
                  style={{ paddingLeft: "2.6rem" }}
                />
              </div>
            </div>

            {/* Upload Invoice */}
            <div
              className="form-group form-item-full"
              style={{ gridColumn: "1 / -1" }}
            >
              <label>Attach Invoice (optional)</label>
              <div className="upload-zone">
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={handleImageChange}
                />
                <div className="upload-icon-wrap">
                  {isExtracting ? (
                    <FaSpinner
                      className="spin"
                      style={{ color: "#fff", fontSize: "1.2rem" }}
                    />
                  ) : (
                    <FaCloudUploadAlt />
                  )}
                </div>
                <h4>
                  {image ? image.name : "Upload invoice to auto-fill fields"}
                </h4>
                <p>Drag & drop or click to browse</p>
                <span className="upload-hint">Supports: JPG, PNG, PDF</span>
                {isExtracting && (
                  <div className="extracting-badge">
                    <FaSpinner className="spin" /> Extracting invoice data…
                  </div>
                )}
                {extracted && !isExtracting && (
                  <div
                    className="extracting-badge"
                    style={{ background: "#dcfce7", color: "#16a34a" }}
                  >
                    <FaCheckCircle /> Fields auto-filled!
                  </div>
                )}
              </div>

              {imagePreview && (
                <div className="receipt-preview">
                  <img src={imagePreview} alt="Invoice preview" />
                  <button
                    className="remove-img"
                    onClick={() => {
                      setImage(null);
                      setImagePreview(null);
                      setExtracted(false);
                    }}
                  >
                    <FaTimes />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="card-footers">
            <button
              className="save-btns"
              onClick={handleSave}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <FaSpinner className="spin" /> Creating…
                </>
              ) : (
                <>
                  <FaSave /> Create Invoice
                </>
              )}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
